import * as Yup from 'yup'
import type { PolicyConfigurationDetail, PolicyConfigurationSections } from './BenefitPolicyService'
import { yupSiblingString } from '~/shared/lib/guards'

/**
 * The editor's form shape, and the translation between it and the API's sections.
 *
 * Every value is held as a string because that is what an input holds; the empty string is the
 * editor's "not set", and only this module knows how each field expresses that back to the API.
 */

/**
 * Field names come from the generated client, not from this file.
 *
 * The labels and the row grouping below are copy and layout, which only the UI can own — but the
 * names they hang off are the API's. Deriving them means a field renamed or dropped backend-side
 * fails to compile here instead of silently sending a key the endpoint ignores.
 */
export type AccumulatorValueField = keyof NonNullable<
  NonNullable<PolicyConfigurationSections['accumulators']>['individual']
>
export type PharmacyField = keyof NonNullable<PolicyConfigurationSections['pharmacy']>
type AccumulatorValues = Record<AccumulatorValueField, string>

/**
 * The network tiers, read off the accumulator field names themselves.
 *
 * Every tier contributes an `<tier>_deductible`, so the set of tiers is already stated by the API
 * — a fourth one added backend-side makes `ACCUMULATOR_TIERS` incomplete at compile time rather
 * than quietly missing a column from the grid.
 */
type TierOf<Field> = Field extends `${infer Tier}_deductible` ? Tier : never
type AccumulatorTierKey = TierOf<AccumulatorValueField>

/** In display order, firefly_guided first. */
export const ACCUMULATOR_TIERS = [
  { key: 'firefly_guided', label: 'Firefly guided' },
  { key: 'self_guided', label: 'Self guided' },
  { key: 'oon', label: 'Out of network' },
] as const satisfies readonly { key: AccumulatorTierKey; label: string }[]

type CoverageAndDates = NonNullable<PolicyConfigurationSections['coverage_and_dates']>
type CoverageField = keyof CoverageAndDates

/**
 * A field the endpoint restricts to a fixed set of values, plus the form's own "not set".
 *
 * The `''` is a DOM constraint rather than a modelling one: a controlled input's value cannot be
 * `null`. Keeping the endpoint's union alongside it is what lets the payload be built without
 * casting the choice back out of a widened `string`.
 */
type ChoiceOf<Field extends CoverageField> = NonNullable<CoverageAndDates[Field]> | ''

/**
 * The fields an input genuinely holds as free text.
 *
 * A date and a decimal are both `string` here because that is what an input gives back, and `''`
 * stands in for the `null` the endpoint accepts. The exceptions below are the fields where the API
 * says more than "some string" and the form has no reason to forget it.
 */
type StringField = Exclude<
  CoverageField | PharmacyField,
  'contract' | 'coverage_product' | 'waiver_disclaimer' | 'id_card_template' | 'accumulator_tiers'
>

/**
 * The form's shape: the API's field names, with the form's own types.
 *
 * Extending the mapped type rather than listing the fields is what makes this bite — a field added
 * to either serializer becomes a missing property on `editorValuesFrom` and `sectionsFrom`, so the
 * build fails instead of the editor quietly having no input for it.
 */
export interface PolicyEditorValues extends Record<StringField, string> {
  contract: number | ''
  coverage_product: number | ''
  id_card_template: ChoiceOf<'id_card_template'>
  accumulator_tiers: ChoiceOf<'accumulator_tiers'>
  individual: AccumulatorValues
  family: AccumulatorValues
}

const text = (value: string | null | undefined) => value ?? ''

const accumulatorValues = (
  accumulator: PolicyConfigurationDetail['individual_accumulator'],
): AccumulatorValues => ({
  oon_deductible: text(accumulator?.oon_deductible),
  oon_out_of_pocket: text(accumulator?.oon_out_of_pocket),
  firefly_guided_deductible: text(accumulator?.firefly_guided_deductible),
  firefly_guided_out_of_pocket: text(accumulator?.firefly_guided_out_of_pocket),
  self_guided_deductible: text(accumulator?.self_guided_deductible),
  self_guided_out_of_pocket: text(accumulator?.self_guided_out_of_pocket),
})

export const editorValuesFrom = (policy: PolicyConfigurationDetail): PolicyEditorValues => {
  // The endpoint takes one mapping at a time, and a configuration carries one per vendor, so the
  // editor shows the first. A second would need a repeater, which no contract needs yet.
  const mapping = policy.plan_mappings[0]
  return {
    contract: policy.contract,
    coverage_product: policy.coverage_product ?? '',
    id_card_template: policy.id_card_template ?? '',
    effective_start: text(policy.effective_start),
    effective_end: text(policy.effective_end),
    electable_start_date: text(policy.electable_start_date),
    network_id: text(policy.network_id),
    accumulator_tiers: policy.accumulator_tiers ?? '',
    description: text(policy.description),
    tpa_vendor: text(mapping?.tpa_vendor),
    group_id: text(mapping?.group_id),
    plan_id: text(mapping?.plan_id),
    individual: accumulatorValues(policy.individual_accumulator),
    family: accumulatorValues(policy.family_accumulator),
    pbm_name: text(policy.pbm_name),
    pbm_url: text(policy.pbm_url),
    rx_bin: text(policy.rx_bin),
    rx_pcn: text(policy.rx_pcn),
    rx_group: text(policy.rx_group),
    pharmacy_disclaimer: text(policy.pharmacy_disclaimer),
  }
}

const EFFECTIVE_END_AFTER_START = 'The effective end date must be after the effective start date.'
const PLAN_MAPPING_NEEDS_ALL = 'A plan mapping needs a TPA vendor, a group id and a plan id.'

export const editorValidationSchema = Yup.object().shape(
  {
    effective_start: Yup.string().required('An effective start date is required.'),
    effective_end: Yup.string()
      .required('An effective end date is required.')
      .test('after-start', EFFECTIVE_END_AFTER_START, (value, context) => {
        const start = yupSiblingString(context, 'effective_start')
        return !value || !start || value > start
      }),
    electable_start_date: Yup.string().required('An electable start date is required.'),
    tpa_vendor: Yup.string().when(['group_id', 'plan_id'], {
      is: (groupId: string, planId: string) => !!groupId || !!planId,
      then: schema => schema.required(PLAN_MAPPING_NEEDS_ALL),
    }),
    group_id: Yup.string().when(['tpa_vendor', 'plan_id'], {
      is: (vendor: string, planId: string) => !!vendor || !!planId,
      then: schema => schema.required(PLAN_MAPPING_NEEDS_ALL),
    }),
    plan_id: Yup.string().when(['tpa_vendor', 'group_id'], {
      is: (vendor: string, groupId: string) => !!vendor || !!groupId,
      then: schema => schema.required(PLAN_MAPPING_NEEDS_ALL),
    }),
  },
  // Each part of the mapping requires the others, which Yup needs declared as cycles.
  [
    ['tpa_vendor', 'group_id'],
    ['tpa_vendor', 'plan_id'],
    ['group_id', 'plan_id'],
  ],
)

type Accumulators = NonNullable<PolicyConfigurationSections['accumulators']>

/** The same six fields the other way, with `''` back to the `null` the endpoint stores. */
const accumulatorPayload = (
  values: AccumulatorValues,
): NonNullable<Accumulators['individual']> => ({
  oon_deductible: values.oon_deductible || null,
  oon_out_of_pocket: values.oon_out_of_pocket || null,
  firefly_guided_deductible: values.firefly_guided_deductible || null,
  firefly_guided_out_of_pocket: values.firefly_guided_out_of_pocket || null,
  self_guided_deductible: values.self_guided_deductible || null,
  self_guided_out_of_pocket: values.self_guided_out_of_pocket || null,
})

const hasAnyAccumulatorValue = (values: AccumulatorValues) =>
  Object.values(values).some(value => value !== '')

/**
 * The whole form, as the API's sections.
 *
 * Every field the operator can see is sent on every save, so what the server stores is what the
 * screen shows. The endpoint treats each field as optional and each save as a complete statement
 * of the sections it carries, which is what makes sending all of them safe.
 *
 * `storedAccumulators` says which accumulator rows the configuration already owns, which is the
 * one thing the values alone cannot tell us — see the grid below.
 */
export const sectionsFrom = (
  values: PolicyEditorValues,
  storedAccumulators: { individual: boolean; family: boolean },
): PolicyConfigurationSections => {
  const coverage: CoverageAndDates = {
    coverage_product: values.coverage_product === '' ? null : values.coverage_product,
    id_card_template: values.id_card_template || null,
    effective_start: values.effective_start,
    effective_end: values.effective_end,
    electable_start_date: values.electable_start_date,
    network_id: values.network_id || null,
    accumulator_tiers: values.accumulator_tiers || null,
    description: values.description || null,
  }
  // Left out rather than coerced when unset: `Number('')` is 0, which the endpoint would reject as
  // an invalid pk rather than read as "no change".
  if (values.contract !== '') coverage.contract = values.contract
  // The mapping travels only once every part is filled — the endpoint refuses an incomplete key,
  // since two thirds of one names no plan. It has no way to express "remove the mapping" either,
  // so an empty set means "leave what is there" rather than "clear it".
  if (values.tpa_vendor && values.group_id && values.plan_id) {
    coverage.tpa_vendor = values.tpa_vendor as CoverageAndDates['tpa_vendor']
    coverage.group_id = values.group_id
    coverage.plan_id = values.plan_id
  }

  const sections: PolicyConfigurationSections = {
    coverage_and_dates: coverage,
    pharmacy: {
      pbm_name: values.pbm_name || null,
      pbm_url: values.pbm_url || null,
      rx_bin: values.rx_bin || null,
      rx_pcn: values.rx_pcn || null,
      rx_group: values.rx_group || null,
      pharmacy_disclaimer: values.pharmacy_disclaimer || null,
      // Deprecated and no longer on the form.
      waiver_disclaimer: true,
    },
  }

  // Each side is decided on its own. A side already backed by a row is always sent, so emptying its
  // last value is a real clear rather than a no-op; a side with neither a row nor a value is left
  // out, because the endpoint creates a `PolicyAccumulator` for every key it receives — six nulls
  // would have an untouched half of the grid spring into existence beside the half being edited.
  const accumulators: Accumulators = {}
  if (storedAccumulators.individual || hasAnyAccumulatorValue(values.individual)) {
    accumulators.individual = accumulatorPayload(values.individual)
  }
  if (storedAccumulators.family || hasAnyAccumulatorValue(values.family)) {
    accumulators.family = accumulatorPayload(values.family)
  }
  if (Object.keys(accumulators).length > 0) sections.accumulators = accumulators

  return sections
}
